<template>
  <v-container fluid pa-0>
    <!-- # ATR decline -->
    <v-row>
      <v-col cols="12">
        <helpers-section-sub-header class="mb-2"> # ATR decline: </helpers-section-sub-header>
        <p>ATR (average true range): ATR used will be the ATR on date of purchase.</p>
        <p>
          If you were to enter <code>2</code> for the # ATR decline then you will exit the position
          if the price of the stock declines by 2ATR from your purchase price. Additional
          accumulation of a security resets the purchase ATR to the ATR calculated on the most
          recent purchase.
        </p>
      </v-col>
      <v-col cols="12">
        <v-row no-gutters>
          <v-checkbox
            v-model="criteria.atr_decline_enabled"
            hide-details
            density="compact"
            class="shrink mr-2"
          />
          <v-text-field
            v-model="criteria.atr_decline"
            :disabled="!criteria.atr_decline_enabled"
            type="number"
            variant="outlined"
            density="compact"
            label="# ATR decline"
            hide-details
            class="shrink"
          />
        </v-row>
      </v-col>
      <v-col cols="12">
        <h4 class="mb-2">Example:</h4>
        <div class="mb-1">You enter <code>2</code> for the # ATR Decline stop.</div>
        <div class="mb-1">
          Assume a security is purchased and its 14-day ATR is <code>5.65</code> and the purchase
          price per share is <code>$181.17</code>.
        </div>
        <div>Your stop would be <code>$181.17 - (2 x 5.65) = $169.87</code></div>
      </v-col>
    </v-row>

    <!-- Stop Loss Exit % -->
    <helpers-section-sub-header class="mt-6"> Stop loss exit %: </helpers-section-sub-header>
    <v-row no-gutters>
      <v-col cols="auto"
        ><v-checkbox
          v-model="criteria.stop_loss_exit_enabled"
          hide-details
          density="compact"
          class="shrink mr-2"
      /></v-col>
      <v-col cols="auto"
        ><v-text-field
          v-model="criteria.stop_loss_exit"
          :disabled="!criteria.stop_loss_exit_enabled"
          type="number"
          suffix="%"
          variant="outlined"
          density="compact"
          class="shrink"
          hide-details
      /></v-col>
    </v-row>

    <!-- Stop Gain Exit % -->
    <helpers-section-sub-header class="mt-6"> Stop gain exit %: </helpers-section-sub-header>
    <v-row no-gutters>
      <v-col cols="auto">
        <v-checkbox
          v-model="criteria.stop_gain_exit_enabled"
          hide-details
          density="compact"
          class="shrink mr-2"
      /></v-col>
      <v-col cols="auto"
        ><v-text-field
          v-model="criteria.stop_gain_exit"
          :disabled="!criteria.stop_gain_exit_enabled"
          type="number"
          min="0"
          suffix="%"
          variant="outlined"
          density="compact"
          class="shrink"
          hide-details
      /></v-col>

      <v-checkbox
        v-model="criteria.stop_gain_exit_realtime"
        label="Simulate real-time trading"
        hint="will sell using same days closing price.  "
        hide-details
        class="shrink mr-2"
      />
    </v-row>

    <!-- Stop ATR Gain Exit -->
    <helpers-section-sub-header class="mt-6"> Stop ATR gain exit: </helpers-section-sub-header>
    <v-row no-gutters>
      <v-col cols="auto"
        ><v-checkbox
          v-model="criteria.stop_atr_gain_exit_enabled"
          hide-details
          density="compact"
          class="shrink mr-2"
      /></v-col>
      <v-col cols="auto"
        ><v-text-field
          v-model="criteria.stop_atr_gain_exit"
          :disabled="!criteria.stop_atr_gain_exit_enabled"
          type="number"
          variant="outlined"
          density="compact"
          min="0"
          label="# of ATR"
          class="shrink"
          hide-details
      /></v-col>
      <v-col cols="auto">
        <v-select
          v-model="criteria.atr_stop_gain_time_period_id"
          :items="atr_time_periods"
          variant="outlined"
          item-title="text"
          density="compact"
          :disabled="!criteria.stop_atr_gain_exit_enabled"
          label="ATR Time Period"
          class="shrink"
          hide-details
      /></v-col>
    </v-row>

    <!-- Stop Loss Exit % -->
    <helpers-section-sub-header class="mt-6"> Trailing ATR Stop Loss: </helpers-section-sub-header>
    <v-row no-gutters>
      <v-col cols="auto">
        <v-checkbox
          v-model="criteria.trailing_atr_stop_loss_exit_enabled"
          hide-details
          density="compact"
          class="shrink mr-2"
      /></v-col>
      <v-col cols="auto"
        ><v-text-field
          v-model="criteria.trailing_atr_stop_loss_number"
          :disabled="!criteria.trailing_atr_stop_loss_exit_enabled"
          type="number"
          label="# ATR"
          variant="outlined"
          density="compact"
          class="shrink"
          hide-details
      /></v-col>
      <v-col cols="auto">
        <v-select
          v-model="criteria.trailing_atr_stop_loss_time_period_id"
          :items="atr_time_periods"
          variant="outlined"
          item-title="text"
          density="compact"
          :disabled="!criteria.trailing_atr_stop_loss_exit_enabled"
          label="ATR Moving Average Time Period"
          hide-details
      /></v-col>
    </v-row>
    <trend />

    <!-- Exit Max Time Held in Days -->
    <helpers-section-sub-header class="mt-6">
      Exit max time held in days:
    </helpers-section-sub-header>
    <v-row no-gutters>
      <v-col cols="auto">
        <v-checkbox
          v-model="criteria.exit_max_time_enabled"
          hide-details
          density="compact"
          class="shrink mr-2"
      /></v-col>
      <v-col cols="auto"
        ><v-text-field
          v-model="criteria.exit_max_time"
          :disabled="!criteria.exit_max_time_enabled"
          type="number"
          min="0"
          variant="outlined"
          density="compact"
          suffix="days"
          class="shrink"
          hide-details
      /></v-col>
    </v-row>

    <!-- Exit at a specified time period specified low -->
    <helpers-section-sub-header class="mt-6">
      Exit at a specified time period Low:
    </helpers-section-sub-header>
    <v-row no-gutters>
      <v-col cols="auto"
        ><v-checkbox
          v-model="criteria.exit_time_period_low_enabled"
          hide-details
          density="compact"
          class="shrink mr-2"
      /></v-col>
      <v-col cols="auto"
        ><v-select
          v-model="criteria.exit_time_period_low_id"
          :items="exit_time_periods"
          variant="outlined"
          density="compact"
          item-title="text"
          :disabled="!criteria.exit_time_period_low_enabled"
          class="shrink"
      /></v-col>

      <v-col cols="12">
        For example, if you choose <code>7 Days</code>, you will exit the position when the stock
        price is at a 7-day low regardless of the price when the position was entered.
      </v-col>
    </v-row>

    <!-- Exit at a specified time period specified high -->
    <helpers-section-sub-header class="mt-6">
      Exit at a specified time period High:
    </helpers-section-sub-header>
    <v-row no-gutters>
      <v-col cols="auto"
        ><v-checkbox
          v-model="criteria.exit_time_period_high_enabled"
          hide-details
          density="compact"
          class="shrink mr-2"
      /></v-col>
      <v-col cols="auto"
        ><v-select
          v-model="criteria.exit_time_period_high_id"
          :items="exit_time_periods"
          variant="outlined"
          density="compact"
          item-title="text"
          :disabled="!criteria.exit_time_period_high_enabled"
          class="shrink"
      /></v-col>

      <v-col cols="12">
        For example, if you choose <code>7 Days</code>, you will exit the position when the stock
        price is at a 7-day high regardless of the price when the position was entered.
      </v-col>
    </v-row>

    <helpers-section-sub-header class="mt-6">
      13F Avg Ranking Change exit:
    </helpers-section-sub-header>
    <v-row no-gutters>
      <v-col cols="auto"></v-col>
      <v-col cols="auto"></v-col>
      <v-checkbox
        v-model="criteria.avg_ranking_exit.enabled"
        hide-details
        density="compact"
        class="shrink ma-0"
      />
      <v-btn @click="show_avg_ranking_exit()"> Configure </v-btn>
      <avg-ranking-dialog-raw ref="avg_ranking_dialog" />
    </v-row>

    <!-- Minimum # days to hold a position -->
    <helpers-section-sub-header class="mt-6">
      Minimum # days to hold a position:
    </helpers-section-sub-header>
    <v-row no-gutters>
      <v-col cols="auto"
        ><v-checkbox
          v-model="criteria.minimum_holding_days_enabled"
          hide-details
          density="compact"
          class="shrink mr-2"
      /></v-col>
      <v-col cols="auto"
        ><v-text-field
          v-model="criteria.minimum_holding_days"
          :disabled="!criteria.minimum_holding_days_enabled"
          type="number"
          min="0"
          variant="outlined"
          density="compact"
          suffix="days"
          class="shrink"
          hide-details
      /></v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <helpers-section-sub-header class="mb-2">
          Stock Price to Use for Sells:
        </helpers-section-sub-header>
        <v-row no-gutters>
          <v-col cols="auto">
            <v-select
              v-model="criteria.stock_price_to_use"
              width="200"
              item-title="text"
              variant="outlined"
              density="compact"
              :items="['closing', 'opening']"
              hide-details
              label="opening or closing prices"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- TEMP: Disabled fields -->
    <helpers-section-sub-header>Signal Specific Overrides:</helpers-section-sub-header>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-checkbox
            v-model="criteria.d13_exit_when_owner_exits"
            hide-details
            density="compact"
            class="mb-1 mt-0 mr-2"
            label="13D/G Specific:  Sell position if original filer discloses <5% ownership"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-checkbox
            v-model="criteria.f13_exit_no_longer_in_group"
            hide-details
            density="compact"
            class="mb-1 mt-0 mr-2"
            label="13F Specific:  Sell position if no longer in your groups top holdings"
          />
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
// Modules
import { mapWritableState } from 'pinia'
import { useAppStore } from '@/store'
import AvgRankingDialog from './_AvgRankingBreakout'
import Trend from './_ExitTrends'
import { markRaw } from 'vue'
const trend = markRaw(Trend)
const AvgRankingDialogRaw = markRaw(AvgRankingDialog)
export default {
  components: {
    AvgRankingDialogRaw,
    trend
  },
  props: {
    ruleType: {
      type: String,
      required: true,
      validator: value => ['long', 'short'].includes(value)
    }
  },
  computed: {
    /* ...mapFields({
      appcriteria: 'app.whaleTraderCriteria',
      atr_time_periods: 'app.atr_time_periods',
      exit_time_periods: 'app.exit_time_periods'
    }),*/
    ...mapWritableState(useAppStore, [
      'whaleTraderCriteria',
      'atr_time_periods',
      'exit_time_periods'
    ]),

    // Computed criteria based on ruleType prop - avoids prop mutation warning
    criteria() {
      return this.whaleTraderCriteria.exit_rules[this.ruleType]
    }
  },
  methods: {
    show_avg_ranking_exit() {
      this.$refs.avg_ranking_dialog.show(this.criteria.avg_ranking_exit, [
        this.criteria.avg_ranking_exit
      ])
    }
  }
}
</script>
